import { useRetrieveIntraSquadMatch, useRetrievePlayer, useRetrieveTeam, } from "../ApiProvider";
import { useRetrieveDivision } from "../api/DivisionApiProvider";
import { useRetrieveLeague } from "../api/LeagueApiProvider";
import { useRetrieveExternalMatch } from "../api/MatchApiProvider";
import { useRetrieveLeagueMatch, useRetrieveSeason, } from "../api/SeasonApiProvider";
import { useRetrieveIntraSquadTournament, useRetrieveIntraSquadTournamentMatch, } from "../api/TournamentApiProvider";
import { useRetrieveTransferRequest } from "../api/TransferRequestApiProvider";
import { useRetrieveVenue } from "../api/VenueApiProvider";
import { playerDisplayName } from "../players/PlayerDisplayNameLink";
import { EntityResolver } from "./routeConfig";
/**
 * Formats an IntraSquadMatch for display in breadcrumbs.
 */
const formatIntraSquadMatch = (match) => {
    if (!match)
        return undefined;
    return `${match.teamAName} vs ${match.teamBName}`;
};
/**
 * Formats an ExternalMatch for display in breadcrumbs.
 */
const formatExternalMatch = (match) => {
    if (!match)
        return undefined;
    return `vs ${match.opponentTeam.name}`;
};
/**
 * Formats a LeagueMatch for display in breadcrumbs.
 */
const formatLeagueMatch = (match) => {
    if (!match)
        return undefined;
    const home = match.homeTeam.name;
    const away = match.awayTeam.name;
    return `${home} v ${away}`;
};
/**
 * Hook that resolves entity names for breadcrumb display.
 * All hooks are called unconditionally (React rules) but only enabled when needed.
 */
export const useBreadcrumbData = (segments) => {
    // Extract entity IDs from segments
    const teamId = segments.find((s) => s.resolver === EntityResolver.Team)?.value;
    const leagueId = segments.find((s) => s.resolver === EntityResolver.League)?.value;
    const venueId = segments.find((s) => s.resolver === EntityResolver.Venue)?.value;
    const playerId = segments.find((s) => s.resolver === EntityResolver.Player)?.value;
    const seasonId = segments.find((s) => s.resolver === EntityResolver.Season)?.value;
    const divisionId = segments.find((s) => s.resolver === EntityResolver.Division)?.value;
    const intraSquadMatchId = segments.find((s) => s.resolver === EntityResolver.IntraSquadMatch)?.value;
    const externalMatchId = segments.find((s) => s.resolver === EntityResolver.ExternalMatch)?.value;
    const intraSquadTournamentId = segments.find((s) => s.resolver === EntityResolver.IntraSquadTournament)?.value;
    const intraSquadTournamentMatchId = segments.find((s) => s.resolver === EntityResolver.IntraSquadTournamentMatch)?.value;
    const transferId = segments.find((s) => s.resolver === EntityResolver.Transfer)?.value;
    const leagueMatchId = segments.find((s) => s.resolver === EntityResolver.LeagueMatch)?.value;
    // Call all hooks unconditionally, but enable only what's needed
    const team = useRetrieveTeam({ id: teamId, enabled: !!teamId });
    const league = useRetrieveLeague({ id: leagueId, enabled: !!leagueId });
    const venue = useRetrieveVenue({ id: venueId, enabled: !!venueId });
    const player = useRetrievePlayer({ id: playerId, enabled: !!playerId });
    const season = useRetrieveSeason({ id: seasonId, enabled: !!seasonId });
    const division = useRetrieveDivision({
        id: divisionId,
        enabled: !!divisionId,
    });
    const intraSquadMatch = useRetrieveIntraSquadMatch({
        id: intraSquadMatchId,
        enabled: !!intraSquadMatchId,
    });
    const externalMatch = useRetrieveExternalMatch({
        id: externalMatchId,
        enabled: !!externalMatchId,
    });
    const intraSquadTournament = useRetrieveIntraSquadTournament({
        id: intraSquadTournamentId,
        enabled: !!intraSquadTournamentId,
    });
    const intraSquadTournamentMatch = useRetrieveIntraSquadTournamentMatch({
        id: intraSquadTournamentMatchId,
        enabled: !!intraSquadTournamentMatchId,
    });
    const transfer = useRetrieveTransferRequest({ id: transferId });
    const leagueMatch = useRetrieveLeagueMatch({
        id: leagueMatchId,
        enabled: !!leagueMatchId,
    });
    // Determine if any enabled query is loading
    const isLoading = (!!teamId && team.isLoading) ||
        (!!leagueId && league.isLoading) ||
        (!!venueId && venue.isLoading) ||
        (!!playerId && player.isLoading) ||
        (!!seasonId && season.isLoading) ||
        (!!divisionId && division.isLoading) ||
        (!!intraSquadMatchId && intraSquadMatch.isLoading) ||
        (!!externalMatchId && externalMatch.isLoading) ||
        (!!intraSquadTournamentId && intraSquadTournament.isLoading) ||
        (!!intraSquadTournamentMatchId && intraSquadTournamentMatch.isLoading) ||
        (!!transferId && transfer.isLoading) ||
        (!!leagueMatchId && leagueMatch.isLoading);
    return {
        [EntityResolver.Team]: team.data?.name,
        [EntityResolver.League]: league.data?.name,
        [EntityResolver.Venue]: venue.data?.name,
        [EntityResolver.Player]: playerDisplayName(player.data),
        [EntityResolver.Season]: season.data?.name,
        [EntityResolver.Division]: division.data?.name,
        [EntityResolver.IntraSquadMatch]: formatIntraSquadMatch(intraSquadMatch.data),
        [EntityResolver.ExternalMatch]: formatExternalMatch(externalMatch.data),
        [EntityResolver.IntraSquadTournament]: intraSquadTournament.data?.tournamentName || "Tournament",
        [EntityResolver.IntraSquadTournamentMatch]: intraSquadTournamentMatch.data
            ? `Round ${intraSquadTournamentMatch.data.round}`
            : undefined,
        [EntityResolver.Transfer]: transfer.data
            ? `Transfer #${transfer.data.id}`
            : undefined,
        [EntityResolver.LeagueMatch]: formatLeagueMatch(leagueMatch.data),
        isLoading,
    };
};
